import {
  Color3,
  CreateRibbon,
  Mesh,
  Scene,
  StandardMaterial,
  Texture,
  Vector3
} from "../../app/babylon";
import { assets } from "../assets";

const SEGMENT_LENGTH = 1.5;
const SLOPE_START_Z = -25;
const SLOPE_LENGTH = 700;
const SLOPE_TOTAL = SLOPE_LENGTH - SLOPE_START_Z;
const GROOMED_HALF = 6;
const POWDER_HALF = 18;
const PITCH = 0.18;
const HILL_AMPLITUDE = 1.6;
const HILL_PERIOD = 70;
const CURVE_AMPLITUDE = 6;
const CURVE_PERIOD_A = 160;
const CURVE_PERIOD_B = 90;
const CURVE_PHASE_B = 1.7;

export interface SlopeSample {
  centerX: number;
  surfaceY: number;
  forwardX: number;
  forwardZ: number;
  rightX: number;
  rightZ: number;
}

export class Slope {
  readonly groomed: Mesh;
  readonly leftPowder: Mesh;
  readonly rightPowder: Mesh;
  readonly length = SLOPE_LENGTH;
  readonly groomedHalfWidth = GROOMED_HALF;
  readonly powderHalfWidth = POWDER_HALF;

  constructor(private readonly scene: Scene) {
    const steps = Math.floor(SLOPE_TOTAL / SEGMENT_LENGTH);

    const leftPowderEdge: Vector3[] = [];
    const leftGroomedEdge: Vector3[] = [];
    const rightGroomedEdge: Vector3[] = [];
    const rightPowderEdge: Vector3[] = [];

    for (let i = 0; i <= steps; i += 1) {
      const z = SLOPE_START_Z + i * SEGMENT_LENGTH;
      const sample = this.sampleAtZ(z);

      const cx = sample.centerX;
      const cy = sample.surfaceY;
      const rx = sample.rightX;
      const rz = sample.rightZ;

      leftPowderEdge.push(
        new Vector3(cx - rx * POWDER_HALF, cy + 0.4, z - rz * POWDER_HALF)
      );
      leftGroomedEdge.push(
        new Vector3(cx - rx * GROOMED_HALF, cy, z - rz * GROOMED_HALF)
      );
      rightGroomedEdge.push(
        new Vector3(cx + rx * GROOMED_HALF, cy, z + rz * GROOMED_HALF)
      );
      rightPowderEdge.push(
        new Vector3(cx + rx * POWDER_HALF, cy + 0.4, z + rz * POWDER_HALF)
      );
    }

    const groomedTex = new Texture(assets.groomedSnow, scene);
    groomedTex.uScale = 1;
    groomedTex.vScale = SLOPE_TOTAL / 6;
    groomedTex.wrapU = Texture.WRAP_ADDRESSMODE;
    groomedTex.wrapV = Texture.WRAP_ADDRESSMODE;
    const groomedMat = new StandardMaterial("groomed-mat", scene);
    groomedMat.diffuseTexture = groomedTex;
    groomedMat.specularColor = new Color3(0.15, 0.18, 0.22);
    groomedMat.diffuseColor = new Color3(1, 1, 1);

    const powderTex = new Texture(assets.powderSnow, scene);
    powderTex.uScale = 4;
    powderTex.vScale = SLOPE_TOTAL / 8;
    powderTex.wrapU = Texture.WRAP_ADDRESSMODE;
    powderTex.wrapV = Texture.WRAP_ADDRESSMODE;
    const powderMat = new StandardMaterial("powder-mat", scene);
    powderMat.diffuseTexture = powderTex;
    powderMat.specularColor = new Color3(0.04, 0.06, 0.08);
    powderMat.diffuseColor = new Color3(1, 1, 1);

    this.groomed = this.buildRibbon(
      "slope-groomed",
      [leftGroomedEdge, rightGroomedEdge],
      groomedMat
    );

    this.leftPowder = this.buildRibbon(
      "slope-powder-left",
      [leftPowderEdge, leftGroomedEdge],
      powderMat
    );

    this.rightPowder = this.buildRibbon(
      "slope-powder-right",
      [rightGroomedEdge, rightPowderEdge],
      powderMat
    );
  }

  private buildRibbon(
    name: string,
    paths: Vector3[][],
    material: StandardMaterial
  ): Mesh {
    const mesh = CreateRibbon(
      name,
      {
        pathArray: paths,
        sideOrientation: Mesh.FRONTSIDE,
        updatable: false
      },
      this.scene
    );
    mesh.material = material;
    mesh.isPickable = false;
    mesh.receiveShadows = true;
    return mesh;
  }

  sampleAtZ(z: number): SlopeSample {
    const centerX = this.centerXAt(z);
    const surfaceY = this.surfaceYAt(z);

    const dz = 0.5;
    const cxAhead = this.centerXAt(z + dz);
    const cyAhead = this.surfaceYAt(z + dz);

    const fxRaw = cxAhead - centerX;
    const fyRaw = cyAhead - surfaceY;
    const fzRaw = dz;
    const flen = Math.sqrt(fxRaw * fxRaw + fyRaw * fyRaw + fzRaw * fzRaw);
    const forwardX = fxRaw / flen;
    const forwardZ = fzRaw / flen;

    const rightHorizLen = Math.sqrt(forwardX * forwardX + forwardZ * forwardZ);
    const rightX = forwardZ / rightHorizLen;
    const rightZ = -forwardX / rightHorizLen;

    return { centerX, surfaceY, forwardX, forwardZ, rightX, rightZ };
  }

  private centerXAt(z: number): number {
    const a = Math.sin((z / CURVE_PERIOD_A) * Math.PI * 2);
    const b = Math.sin((z / CURVE_PERIOD_B) * Math.PI * 2 + CURVE_PHASE_B);
    return CURVE_AMPLITUDE * a + CURVE_AMPLITUDE * 0.45 * b;
  }

  private surfaceYAt(z: number): number {
    const fall = -PITCH * z;
    const hill = -HILL_AMPLITUDE * Math.sin((z / HILL_PERIOD) * Math.PI * 2);
    return fall + hill;
  }
}
